import React from 'react';
import _ from 'lodash';
import * as Ramda from 'ramda';

const isNotInsiderThreat = Ramda.compose(
  Ramda.not,
  Ramda.anyPass([
    Ramda.equals('insiderThreatCF'),
    Ramda.equals('insiderThreat'),
  ])
);

const isNotCompanyFactor = Ramda.compose(
  Ramda.not,
  Ramda.equals('companyAggregate')
);

/** Displayable = not Insider Threat and not the aggregate. */
export const isDisplayableFactor = Ramda.allPass([
  isNotInsiderThreat,
  isNotCompanyFactor,
]);

/**
 * Normalizes per-factor CF rows for a policy. The factor set is always driven
 * by the live risk `details`, so "Last Underwriting" and "Latest Risk" show the
 * same factors and stay comparable. `company` comes from the policy's locked
 * `cowbellRiskFactors` on "Last Underwriting" — falling back to the live value
 * for factors the snapshot predates (e.g. Supply Chain on older policies) —
 * and from live risk on "Latest Risk"; ranges are always live.
 */
export const useNormalizeCowbellFactorRiskDetails = ({
  accountRiskDetails,
  latestRiskSelected,
  policy,
}: any) => {
  return React.useMemo(() => {
    const { details } = accountRiskDetails;
    // `|| {}` (not just the _.get default): lodash only substitutes the default
    // for `undefined`, so an explicit `factors: null` would slip through and
    // crash the indexing below on both toggle states.
    const policyCowbellFactors =
      _.get(policy, 'cowbellRiskFactors.factors') || {};

    return Object.keys(details)
      .filter(isDisplayableFactor)
      .map((factorKey) => {
        const liveDetail = details[factorKey];
        const lockedCompany = policyCowbellFactors[`${factorKey}CF`];
        const useLockedValue = !latestRiskSelected && lockedCompany != null;
        return {
          factorKey,
          company: useLockedValue ? lockedCompany : liveDetail.company,
          min: liveDetail.min,
          max: liveDetail.max,
          industry: liveDetail.industry,
        };
      })
      .sort((a, b) => (a.factorKey < b.factorKey ? -1 : 1));
  }, [accountRiskDetails, latestRiskSelected, policy]);
};

export default useNormalizeCowbellFactorRiskDetails;
